import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';

//A booking that has changed, and the days it touched. A move touches two: the day it left and
//the day it arrived on. Days are YYYYMMDD, as the venue hub's bookingEvent_ names use them.
export interface bookingchange {
  bookingId: string;
  days: string[];
}

//Tells the screens that show bookings when one has been changed from somewhere else in the
//Dashboard - today, the AI assistant moving a booking - so they reload instead of showing it
//where it used to be.
//
//The venue hub (SignalR) already announces saves, but per day, and a screen showing the day a
//booking was moved away from may not be told. This names both days, and reaches the Dashboard's
//other tabs in the same browser too.
@Injectable({
  providedIn: 'root'
})
export class BookingChangesService {

  private subject = new Subject<bookingchange>();
  readonly changes: Observable<bookingchange> = this.subject.asObservable();

  private channel: BroadcastChannel = null;

  constructor(private zone: NgZone) {
    try {
      this.channel = new BroadcastChannel('wannabook-booking-changes');

      //Messages from other tabs arrive outside Angular, so they are brought back in to repaint.
      this.channel.onmessage = event => this.zone.run(() => this.subject.next(event.data as bookingchange));
    }
    catch {
      //No BroadcastChannel (very old browser): this tab is still told, other tabs are not.
      this.channel = null;
    }
  }

  announce(change: bookingchange): void {
    this.subject.next(change);

    try {
      this.channel?.postMessage(change);
    }
    catch {
      //Other tabs simply are not told.
    }
  }

  //A screen's date as a day key, read with local getters as the screens' own hub names are.
  static dayKey(date: Date): string {
    const d = new Date(date);
    return d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
  }

  //A booking time as a day key, read as written - booking times are the venue's wall clock with
  //a Z on the end, so turning them into a Date first could move a late booking to the next day.
  static dayKeyOfBookingTime(value: any): string {
    const text = (value ?? '').toString();
    return text.length >= 10 ? text.substring(0, 4) + text.substring(5, 7) + text.substring(8, 10) : '';
  }

  //Whether a change touched the day a screen is showing.
  static affects(change: bookingchange, date: Date): boolean {
    return date != null && (change?.days ?? []).includes(BookingChangesService.dayKey(date));
  }

  //Whether a change touched any day in a range.
  static affectsRange(change: bookingchange, from: Date, to: Date): boolean {
    if (from == null || to == null) {
      return false;
    }

    const first = BookingChangesService.dayKey(from);
    const last = BookingChangesService.dayKey(to);

    return (change?.days ?? []).some(day => day >= first && day <= last);
  }
}
